import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthChange, 
  signInWithGoogle, 
  signInWithFacebook, 
  signInWithApple,
  signInWithEmail,
  signUpWithEmail,
  resetPassword,
  logOut,
  getIdToken 
} from '../config/firebase';
import toast from 'react-hot-toast';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        const userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          provider: firebaseUser.providerData[0]?.providerId || 'password'
        };
        console.log('🔐 Auth State Changed: User signed in', {
          uid: userData.uid,
          email: userData.email,
          displayName: userData.displayName,
          provider: userData.provider,
          timestamp: new Date().toISOString()
        });
        setUser(userData);
        
        // Store user profile in backend - disabled until Firestore is enabled
        // try {
        //   const token = await getIdToken();
        //   await fetch('/api/v1/users/profile', {
        //     method: 'POST',
        //     headers: {
        //       'Content-Type': 'application/json',
        //       'Authorization': `Bearer ${token}`
        //     },
        //     body: JSON.stringify(userData)
        //   });
        // } catch (error) {
        //   console.error('Error syncing user profile:', error);
        // }
      } else {
        // User is signed out
        console.log('🔓 Auth State Changed: User signed out', {
          timestamp: new Date().toISOString()
        });
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const handleSignInWithGoogle = async () => {
    try {
      console.log('🔄 Attempting Google sign in...');
      setLoading(true);
      const result = await signInWithGoogle();
      console.log('✅ Google sign in successful', {
        uid: result.user?.uid,
        email: result.user?.email,
        displayName: result.user?.displayName,
        timestamp: new Date().toISOString()
      });
      setShowLoginModal(false);
      toast.success('Successfully signed in with Google!');
    } catch (error) {
      console.error('❌ Google sign in failed:', {
        error: error.message,
        code: error.code,
        timestamp: new Date().toISOString()
      });
      toast.error(error.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleSignInWithFacebook = async () => {
    try {
      console.log('🔄 Attempting Facebook sign in...');
      setLoading(true);
      const result = await signInWithFacebook();
      console.log('✅ Facebook sign in successful', {
        uid: result.user?.uid,
        email: result.user?.email,
        displayName: result.user?.displayName,
        timestamp: new Date().toISOString()
      });
      setShowLoginModal(false);
      toast.success('Successfully signed in with Facebook!');
    } catch (error) {
      console.error('❌ Facebook sign in failed:', {
        error: error.message,
        code: error.code,
        timestamp: new Date().toISOString()
      });
      toast.error(error.message || 'Failed to sign in with Facebook');
    } finally {
      setLoading(false);
    }
  };

  const handleSignInWithApple = async () => {
    try {
      setLoading(true);
      await signInWithApple();
      setShowLoginModal(false);
      toast.success('Successfully signed in with Apple!');
    } catch (error) {
      console.error('Apple sign in error:', error);
      toast.error(error.message || 'Failed to sign in with Apple');
    } finally {
      setLoading(false);
    }
  };

  const handleSignInWithEmail = async (email, password) => {
    try {
      console.log('🔄 Attempting email sign in...', { email });
      setLoading(true);
      const result = await signInWithEmail(email, password);
      console.log('✅ Email sign in successful', {
        uid: result.user?.uid,
        email: result.user?.email,
        displayName: result.user?.displayName,
        timestamp: new Date().toISOString()
      });
      setShowLoginModal(false);
      toast.success('Successfully signed in!');
    } catch (error) {
      console.error('❌ Email sign in failed:', {
        email,
        error: error.message,
        code: error.code,
        timestamp: new Date().toISOString()
      });
      if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email');
      } else if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password');
      } else {
        toast.error(error.message || 'Failed to sign in');
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSignUpWithEmail = async (email, password, displayName) => {
    try {
      console.log('🔄 Attempting email sign up...', { email, displayName });
      setLoading(true);
      const result = await signUpWithEmail(email, password);
      
      // Update display name if provided
      if (displayName && result.user) {
        await result.user.updateProfile({ displayName });
      }
      
      console.log('✅ Email sign up successful', {
        uid: result.user?.uid,
        email: result.user?.email,
        displayName: displayName || result.user?.displayName,
        timestamp: new Date().toISOString()
      });
      setShowLoginModal(false);
      toast.success('Account created successfully!');
    } catch (error) {
      console.error('❌ Email sign up failed:', {
        email,
        error: error.message,
        code: error.code,
        timestamp: new Date().toISOString()
      });
      if (error.code === 'auth/email-already-in-use') {
        toast.error('An account already exists with this email');
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password should be at least 6 characters');
      } else {
        toast.error(error.message || 'Failed to create account');
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (email) => {
    try {
      setLoading(true);
      await resetPassword(email);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email');
      } else {
        toast.error(error.message || 'Failed to send reset email');
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleLogOut = async () => {
    try {
      console.log('🔄 Attempting sign out...', {
        currentUser: user?.email,
        uid: user?.uid
      });
      setLoading(true);
      await logOut();
      console.log('✅ Sign out successful', {
        timestamp: new Date().toISOString()
      });
      toast.success('Successfully signed out');
    } catch (error) {
      console.error('❌ Sign out failed:', {
        error: error.message,
        code: error.code,
        timestamp: new Date().toISOString()
      });
      toast.error('Failed to sign out');
    } finally {
      setLoading(false);
    }
  };

  const requireAuth = (callback) => {
    if (user) {
      callback();
    } else {
      setShowLoginModal(true);
    }
  };

  const value = {
    user,
    loading,
    showLoginModal,
    setShowLoginModal,
    signInWithGoogle: handleSignInWithGoogle,
    signInWithFacebook: handleSignInWithFacebook,
    signInWithApple: handleSignInWithApple,
    signInWithEmail: handleSignInWithEmail,
    signUpWithEmail: handleSignUpWithEmail,
    resetPassword: handleResetPassword,
    logOut: handleLogOut,
    requireAuth,
    getIdToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};